import React, { useState, useEffect } from 'react';
import { Toast } from './Toast';
import type { ToastColor, ToastPosition } from './Toast.types';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface ToastData {
  id: string;
  title?: string;
  message: string;
  description?: string; // IMPORTANTE: Agregar explicitly 'description' para arreglar el error de Typescript en MasterCrud
  color: ToastColor;
  icon?: any;
  position?: ToastPosition;
  duration?: number;
}

let addToastRef: (toast: Omit<ToastData, 'id'>) => void = () => {
  console.warn('ToastProvider no está montado. No se puede mostrar el toast.');
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  useEffect(() => {
    addToastRef = (newToast) => {
      const id = Math.random().toString(36).substr(2, 9);
      setToasts((prev) => [...prev, { ...newToast, id }]);
    };
  }, []);

  const handleClose = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <>
      {children}
      <div className="fixed inset-0 pointer-events-none z-[100] flex flex-col gap-2 p-4 justify-end items-end h-screen w-screen" style={{ top: 0, left: 0, right: 0, bottom: 0}}>
         {/* Renderizamos de forma individual, pero como Toast ya trae clases de fix y posiciones, modificamos un poco como se renderiza en viewport para no sobreponerlos ciegamente si hay 2.*/}
         {toasts.map((t) => (
            <Toast
              key={t.id}
              title={t.title}
              message={t.message}
              color={t.color}
              icon={t.icon}
              position={t.position || 'bottom-right'}
              duration={t.duration || 5000}
              autoClose={true}
              isVisible={true}
              onClose={() => handleClose(t.id)}
            />
         ))}
      </div>
    </>
  );
};

export const toast = Object.assign(
  (message: string, options?: Partial<Omit<ToastData, 'id' | 'message'>>) => {
    // Si viene la palabra description por compatibilidad, la unificamos al mensaje
    const finalMessage = options?.description ? `${message}: ${options.description}` : message;
    addToastRef({ message: finalMessage, color: 'default', ...options });
  },
  {
    success: (message: string, options?: Partial<Omit<ToastData, 'id' | 'message'>>) => {
      const finalMessage = options?.description ? `${message}: ${options.description}` : message;
      addToastRef({ message: finalMessage, color: 'green', icon: CheckCircleIcon, ...options });
    },
    error: (message: string, options?: Partial<Omit<ToastData, 'id' | 'message'>>) => {
      const finalMessage = options?.description ? `${message}: ${options.description}` : message;
      addToastRef({ message: finalMessage, color: 'red', icon: XCircleIcon, ...options });
    },
    warning: (message: string, options?: Partial<Omit<ToastData, 'id' | 'message'>>) => {
      const finalMessage = options?.description ? `${message}: ${options.description}` : message;
      addToastRef({ message: finalMessage, color: 'yellow', icon: ExclamationTriangleIcon, ...options });
    },
    info: (message: string, options?: Partial<Omit<ToastData, 'id' | 'message'>>) => {
       const finalMessage = options?.description ? `${message}: ${options.description}` : message;
      addToastRef({ message: finalMessage, color: 'blue', icon: InformationCircleIcon, ...options });
    }
  }
);
